import { Controller } from '@hotwired/stimulus';
import { loadScript } from '@paypal/paypal-js';
import Rails from '@rails/ujs';
import * as Sentry from '@sentry/browser';
/*
 * Description
 * -------
 *
 * Handles the PayPal payment method change.
 *
 * Targets
 * -------
 *
 * form                    - (required) - form element
 * paypalButton            - (required) - element to render the PayPal button
 * paymentMethodTokenInput - (required) - hidden input for the payment method token
 *
 * Values
 * -------
 *
 * paypalSetupToken - (required) - PayPal setup token
 * paypalIdToken    - (required) - PayPal ID token
 * paypalClientId   - (required) - PayPal client ID
 *
 * Usage
 * -------
 *
 * <div
 *   data-controller="paypal-payment-method"
 *   data-paypal-payment-method-paypal-setup-token-value="..."
 *   data-paypal-payment-method-paypal-id-token-value="..."
 *   data-paypal-payment-method-paypal-client-id-value="..."
 * >
 *   <form data-paypal-payment-method-target="form">
 *     <input data-paypal-payment-method-target="paymentMethodTokenInput" type="hidden" />
 *     <button data-paypal-payment-method-target="paypalButton">(...)</button>
 *   </form>
 * </div>
 */
export default class default_1 extends Controller {
    static targets = ['form', 'paypalButton', 'paymentMethodTokenInput'];
    static values = {
        setupTokenPath: String,
        paypalIdToken: String,
        paypalClientId: String,
    };
    async connect() {
        loadScript({ clientId: this.paypalClientIdValue, dataUserIdToken: this.paypalIdTokenValue }).then((paypal) => {
            paypal
                ?.Buttons?.({
                locale: 'en_US',
                style: {
                    color: 'gold',
                    label: 'pay',
                    height: 25,
                },
                createVaultSetupToken: async () => {
                    const data = await this.fetchPaypalSetupToken();
                    return data;
                },
                // @ts-ignore
                onApprove: ({ vaultSetupToken }) => {
                    this.paypalButtonTarget.classList.add('disabled');
                    this.handlePaymentMethod(vaultSetupToken);
                },
                onClick: () => {
                    this.paypalButtonTarget.classList.add('disabled');
                },
                onCancel: () => {
                    this.paypalButtonTarget.classList.remove('disabled');
                },
                onError: (error) => {
                    this.paypalButtonTarget.classList.remove('disabled');
                    Sentry.captureException(error);
                },
            })
                .render(this.paypalButtonTarget)
                .then(() => {
                this.paypalButtonTarget.classList.remove('disabled');
            });
        });
    }
    async fetchPaypalSetupToken() {
        const response = await fetch(this.setupTokenPathValue, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok)
            throw new Error('Failed to fetch PayPal setup token');
        const data = await response.json();
        return data.setupToken;
    }
    handlePaymentMethod(payment_method_id) {
        this.paymentMethodTokenInputTarget.value = payment_method_id;
        Rails.fire(this.formTarget, 'submit');
    }
}
